import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';
import {
  useSyncList,
  SyncStateTabs,
  Pager,
  SyncStateBadge,
  ListStates,
  SortableTh,
} from './SyncStatusBrowser';
import { TextFilter } from './TransactionFilters';
import { ViewHeader } from './DashboardLayout';
import { useConnection, stripeCustomerUrl, hubspotContactUrl } from './links';
import { ExtLink } from './ExtLink';
import type { CustomerListEntry } from './types';

/**
 * Dashboard → Customers (M10b).
 *
 * The customer sync-status browser. Lists ALL customer_mappings with their
 * Contact link state + per-customer sub/invoice counts.
 *
 * Layout: the first two columns are the Stripe + HubSpot ids (deep-linked),
 * then Name and Email as their own columns, then the sub/invoice counts. Two
 * dedicated text filters — "Find by name" and "Find by email" — replace the
 * old generic search box (both narrow the result set, server-side).
 *
 * Per-row action: **Relink** (only shown for broken customers). Customers can
 * only ever be `synced` or `broken` — they are never ignored (root objects;
 * ignoring would strand their subs + invoices). When the HubSpot Contact was
 * deleted, the customer handler flips sync_state='broken' (M10b fix); Relink
 * fetches the customer from Stripe + recreates the Contact → synced.
 *
 * Reference: docs/features/customer-sync.md
 *            src/do/handlers/adminPanel.ts relinkCustomer
 *            docs/cross-cutting/data-model.md §1.1 (customers never reach ignored)
 */
export function Customers() {
  // Dedicated name/email filters, debounced 350ms before hitting the DO.
  const [nameInput, setNameInput] = useState('');
  const [emailInput, setEmailInput] = useState('');
  const nameQuery = useDebounced(nameInput, 350);
  const emailQuery = useDebounced(emailInput, 350);

  const list = useSyncList<CustomerListEntry>('admin/customers', 25, {
    name: nameQuery || undefined,
    email: emailQuery || undefined,
  });
  const {
    data,
    loading,
    error,
    refetch,
    state,
    setState,
    sort,
    dir,
    toggleSort,
    setOffset,
  } = list;

  const filterActive = nameQuery.length > 0 || emailQuery.length > 0;

  return (
    <>
      <ViewHeader
        title="Customers"
        subtitle="Stripe Customer ↔ HubSpot Contact linkage state."
      />
      <SyncStateTabs state={state} setState={setState} hidePending />

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <TextFilter
          label="Find by name"
          value={nameInput}
          onChange={setNameInput}
        />
        <TextFilter
          label="Find by email"
          value={emailInput}
          onChange={setEmailInput}
        />
        {filterActive && (
          <button
            type="button"
            onClick={() => {
              setNameInput('');
              setEmailInput('');
            }}
            className="text-xs text-ink-400 hover:text-ink-700"
          >
            Clear filters
          </button>
        )}
      </div>

      {(loading || error || (data && data.items.length === 0)) && (
        <ListStates
          loading={loading}
          error={error}
          empty={!!data && data.items.length === 0}
          onRetry={refetch}
        />
      )}

      {data && data.items.length > 0 && (
        <>
          <div className="overflow-hidden rounded-xl border border-ink-200 bg-white">
            <table className="w-full text-sm">
              <thead className="bg-ink-50 text-left text-xs uppercase tracking-wide text-ink-500">
                <tr>
                  <th className="px-4 py-2 font-medium">Stripe customer</th>
                  <SortableTh
                    label="Name"
                    sortKey="name"
                    activeSort={sort}
                    sortDir={dir}
                    onSort={toggleSort}
                  />
                  <SortableTh
                    label="Email"
                    sortKey="email"
                    activeSort={sort}
                    sortDir={dir}
                    onSort={toggleSort}
                  />
                  <th className="px-4 py-2 text-center font-medium">Subs</th>
                  <th className="px-4 py-2 text-center font-medium">Invoices</th>
                  <th className="px-4 py-2 font-medium">State</th>
                  <th className="px-4 py-2 text-right font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-100">
                {data.items.map((c) => (
                  <CustomerRow key={c.stripeCustomerId} customer={c} onChanged={refetch} />
                ))}
              </tbody>
            </table>
          </div>
          <Pager
            total={data.total}
            offset={data.offset}
            pageSize={data.limit}
            setOffset={setOffset}
          />
        </>
      )}
    </>
  );
}

/**
 * Debounce a fast-changing string by `delay` ms. Returns the latest value only
 * after the user stops typing. Used by the name/email filters so each keystroke
 * doesn't fire a DO round-trip.
 */
function useDebounced(value: string, delay: number): string {
  const [debounced, setDebounced] = useState(value);
  const ref = useRef<number | undefined>(undefined);
  useEffect(() => {
    window.clearTimeout(ref.current);
    ref.current = window.setTimeout(() => setDebounced(value.trim()), delay);
    return () => window.clearTimeout(ref.current);
  }, [value, delay]);
  return debounced;
}

function CustomerRow({
  customer,
  onChanged,
}: {
  customer: CustomerListEntry;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const { portalId, stripeAccountId } = useConnection();
  const stripeUrl = stripeCustomerUrl(stripeAccountId, customer.stripeCustomerId);
  const hsUrl = hubspotContactUrl(portalId, customer.hubspotContactId);

  const relink = useCallback(async () => {
    setBusy(true);
    try {
      await api.post('admin/customer/relink', {
        stripeCustomerId: customer.stripeCustomerId,
      });
      onChanged();
    } catch (err) {
      alert(
        `Relink failed: ${err instanceof Error ? err.message : String(err)}`
      );
    } finally {
      setBusy(false);
    }
  }, [customer.stripeCustomerId, onChanged]);

  return (
    <tr className="hover:bg-ink-50/50">
      <td className="px-4 py-2">
        {stripeUrl ? (
          <ExtLink href={stripeUrl} label={customer.stripeCustomerId} title="View in Stripe" />
        ) : (
          <div className="font-mono text-xs text-ink-400">
            {customer.stripeCustomerId}
          </div>
        )}
        {customer.hubspotContactId ? (
          <div className="mt-0.5 font-mono text-xs text-ink-400">
            {hsUrl ? (
              <ExtLink href={hsUrl} label={customer.hubspotContactId} title="HubSpot Contact" />
            ) : (
              customer.hubspotContactId
            )}
          </div>
        ) : (
          <div className="mt-0.5 text-xs text-ink-400">missing</div>
        )}
      </td>
      <td className="px-4 py-2 font-medium text-ink-800">
        {customer.name || '(unnamed)'}
      </td>
      <td className="px-4 py-2 text-ink-700">
        {customer.email && customer.email !== '(none)' ? (
          customer.email
        ) : (
          <span className="text-ink-400">—</span>
        )}
      </td>
      <td className="px-4 py-2 text-center text-ink-700">
        {customer.subscriptionCount}
      </td>
      <td className="px-4 py-2 text-center text-ink-700">
        {customer.invoiceCount}
      </td>
      <td className="px-4 py-2">
        <SyncStateBadge state={customer.syncState} />
      </td>
      <td className="px-4 py-2 text-right">
        {customer.syncState === 'broken' && (
          <button
            className="btn-ghost px-2 py-1 text-xs"
            disabled={busy}
            onClick={relink}
            title="Recreate the deleted HubSpot Contact from a fresh Stripe fetch"
          >
            {busy ? 'Relinking…' : 'Relink'}
          </button>
        )}
        {customer.syncState === 'synced' && (
          <span className="text-xs text-ink-400">linked ✓</span>
        )}
      </td>
    </tr>
  );
}
